import React from "react";
import { createPortal } from "react-dom";
import CheckoutPaymentContent, {
  type CheckoutPaymentVariant,
} from "./CheckoutPaymentContent";
import { CHECKOUT_PAYMENT_MODAL_MAX_WIDTH } from "./checkoutPaymentTheme";

export type CheckoutPaymentModalProps = {
  open: boolean;
  onClose: () => void;
  token: string;
  variant: CheckoutPaymentVariant;
  order: Record<string, unknown> | null;
  totalAmount: number;
  supplierId: string;
  payerUserId: string;
  payerEmail: string;
  paidById: string;
  paidByName: string;
  onPaymentComplete: (orderId: string) => void;
  onClearCart: () => void;
};

function CardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const CheckoutPaymentModal: React.FC<CheckoutPaymentModalProps> = ({
  open,
  onClose,
  token,
  variant,
  order,
  totalAmount,
  supplierId,
  payerUserId,
  payerEmail,
  paidById,
  paidByName,
  onPaymentComplete,
  onClearCart,
}) => {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const orderNumber = String(
    (order as { order_number?: string })?.order_number ?? ""
  ).trim();

  const formattedTotal = `$${Number(totalAmount || 0).toFixed(2)}`;

  const modal = (
    <>
      <div
        id="checkoutPaymentModalRoot"
        className="cp-modal-overlay"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          className="cp-modal"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cp-modal__head">
            <div className="cp-modal__head-icon">
              <CardIcon />
            </div>
            <span className="cp-modal__head-title">Complete Payment</span>
            <div className="cp-modal__head-meta">
              {orderNumber ? (
                <span className="cp-modal__head-order">Order {orderNumber}</span>
              ) : null}
              {orderNumber ? <span className="cp-modal__head-sep">&bull;</span> : null}
              <span className="cp-modal__head-total">
                <span className="cp-modal__amt-label">Total</span>
                <span className="cp-modal__amt-value">{formattedTotal}</span>
              </span>
            </div>
            <div className="cp-modal__head-divider" />
            <button
              type="button"
              className="cp-modal__head-close"
              aria-label="Close"
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>

          <div className="cp-modal__body">
            <CheckoutPaymentContent
              open={open}
              token={token}
              variant={variant}
              order={order}
              totalAmount={totalAmount}
              supplierId={supplierId}
              payerUserId={payerUserId}
              payerEmail={payerEmail}
              paidById={paidById}
              paidByName={paidByName}
              onPaymentComplete={onPaymentComplete}
              onClearCart={onClearCart}
            />
          </div>

          <div className="cp-modal__foot">
            Powered by <b>RealEsy</b>
          </div>
        </div>
      </div>

      <style>{`
        #checkoutPaymentModalRoot.cp-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 1060;
          background: rgba(15, 23, 42, 0.45);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 40px 16px;
          overflow-y: auto;
        }
        #checkoutPaymentModalRoot .cp-modal {
          width: 100%;
          max-width: ${CHECKOUT_PAYMENT_MODAL_MAX_WIDTH}px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 24px 60px rgba(16, 24, 40, 0.18);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          max-height: calc(100vh - 80px);
        }
        #checkoutPaymentModalRoot .cp-modal__head {
          display: flex;
          align-items: center;
          gap: 14px;
          background: #eef3fe;
          padding: 15px 22px;
          border-bottom: 1px solid #dde6fb;
          flex-shrink: 0;
        }
        #checkoutPaymentModalRoot .cp-modal__head-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #fff;
          border: 1px solid #d4e0fb;
          box-shadow: 0 1px 2px rgba(59, 111, 232, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #3B6FE8;
          flex-shrink: 0;
        }
        #checkoutPaymentModalRoot .cp-modal__head-icon svg {
          width: 21px;
          height: 21px;
        }
        #checkoutPaymentModalRoot .cp-modal__head-title {
          flex-shrink: 0;
          font-size: 17px;
          font-weight: 600;
          color: #111827;
        }
        #checkoutPaymentModalRoot .cp-modal__head-meta {
          display: flex;
          align-items: baseline;
          gap: 11px;
          margin-left: auto;
        }
        #checkoutPaymentModalRoot .cp-modal__head-sep {
          color: #c2c9d4;
          font-size: 13px;
          line-height: 1;
        }
        #checkoutPaymentModalRoot .cp-modal__head-order {
          font-size: 13px;
          color: #6b7280;
          white-space: nowrap;
        }
        #checkoutPaymentModalRoot .cp-modal__head-total {
          display: flex;
          align-items: baseline;
          gap: 7px;
          white-space: nowrap;
        }
        #checkoutPaymentModalRoot .cp-modal__amt-label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          color: #6b7280;
        }
        #checkoutPaymentModalRoot .cp-modal__amt-value {
          font-size: 18px;
          font-weight: 700;
          color: #111827;
        }
        #checkoutPaymentModalRoot .cp-modal__head-divider {
          width: 1px;
          height: 26px;
          background: #d7e1f7;
          flex-shrink: 0;
        }
        #checkoutPaymentModalRoot .cp-modal__head-close {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #6b7280;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
          flex-shrink: 0;
          padding: 0;
        }
        #checkoutPaymentModalRoot .cp-modal__head-close:hover {
          background: #dfe7f9;
        }
        #checkoutPaymentModalRoot .cp-modal__head-close svg {
          width: 18px;
          height: 18px;
        }
        #checkoutPaymentModalRoot .cp-modal__body {
          padding: 24px 22px 20px;
          overflow-y: auto;
          flex: 1 1 auto;
          min-height: 0;
          -webkit-overflow-scrolling: touch;
        }
        #checkoutPaymentModalRoot .cp-modal__foot {
          border-top: 1px solid #e2e5ea;
          padding: 14px 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 12px;
          color: #9ca3af;
          flex-shrink: 0;
        }
        #checkoutPaymentModalRoot .cp-modal__foot b {
          color: #3B6FE8;
          font-weight: 600;
        }
        @media (max-width: 520px) {
          #checkoutPaymentModalRoot .cp-modal__head-sep,
          #checkoutPaymentModalRoot .cp-modal__head-order {
            display: none;
          }
          #checkoutPaymentModalRoot.cp-modal-overlay {
            padding: 16px 12px;
          }
        }
      `}</style>
    </>
  );

  return createPortal(modal, document.body);
};

export default CheckoutPaymentModal;
